/**
 * ניטור בריאות הסוקט (SignalR) — יומן אירועים + מדדים מצטברים בלוקאל סטורג'.
 *
 * מטרה: לאבחן את התופעה שבה הפרונט מפסיק להתעדכן בזמן אמת (הודעות נכנסות,
 * מוני לא-נקראו, סדר שיחות) עד לרענון ידני. היומן שורד רענון דף, כך שאפשר
 * לנתח בדיעבד מה קרה לפני שהמשתמש נאלץ לרענן.
 *
 * חשודים עיקריים שהמוניטור מזהה ומסמן:
 *  - `timer-throttle`   — הדפדפן השהה טיימרים (שינה / טאב ברקע) → keepAlive של
 *                         SignalR מפסיק לרוץ והחיבור עלול למות בשקט.
 *  - `silent-connected` — החיבור מדווח Connected אך לא התקבל שום אירוע זמן רב
 *                         (חשד ל"סוקט זומבי").
 *  - `no-global-listeners` — יש חיבור פעיל אבל המאזינים הגלובליים אינם רשומים
 *                         (התנתקות שקטה של ההאזנה — אירועים מגיעים ולא מטופלים).
 *  - `handler-error`    — אירוע התקבל מהסוקט אך המטפל שלו קרס → העדכון לא השתקף
 *                         ב-UI למרות שהסוקט תקין.
 *  - ניתוקים / כשלונות חיבור / reconnect (נתיב A ונתיב B).
 *
 * הרישום הוא תצפית בלבד — אין שום שינוי בלוגיקת החיבור או בטיפול באירועים.
 */

export type SocketHealthCategory =
  | 'connection'
  | 'browser'
  | 'listeners'
  | 'event'
  | 'watchdog'
  | 'sync'
  | 'error';

export type SocketHealthSeverity = 'info' | 'warning' | 'error';

export type SocketHealthEntry = {
  id: string;
  /** epoch ms */
  at: number;
  category: SocketHealthCategory;
  type: string;
  severity: SocketHealthSeverity;
  details?: Record<string, unknown>;
  /** אירועים זהים סמוכים מאוחדים לשורה אחת עם מונה */
  count?: number;
};

export type SocketHealthMetrics = {
  pageLoads: number;
  connectSuccess: number;
  connectFailures: number;
  unexpectedCloses: number;
  /** נתיב A — reconnect אוטומטי של SignalR */
  reconnects: number;
  /** נתיב B — החייאת חיבור שנסגר לגמרי */
  revivals: number;
  eventsTotal: number;
  eventCounts: Record<string, number>;
  lastEventAt: number | null;
  lastEventType: string | null;
  lastConnectedAt: number | null;
  lastDisconnectedAt: number | null;
  timerThrottleDetections: number;
  silentConnectedSuspicions: number;
  noListenersWarnings: number;
  handlerErrors: number;
  resyncs: number;
  updatedAt: number;
};

export type SocketHealthProbe = () => {
  state: string;
  connectionId: string | null;
};

/**
 * תוצאת הטיפול בהודעת צ'אט שהגיעה בסוקט — הרשומה שסוגרת את הפער בין "האירוע
 * התקבל" לבין "מה קרה לו במסך". נשמרת במאגר ייעודי שלא נמחק על ידי רעש היומן,
 * כך שכל ייצוא עונה חד-משמעית על "האם הודעה X הגיעה ומה נעשה איתה".
 */
export type SocketMessageOutcome =
  | 'applied'
  | 'applied-note-no-list-bump'
  | 'dropped-empty-company-tag'
  | 'dropped-foreign-company'
  | 'contact-missing-recovery'
  | 'contact-missing-recovery-chat-applied'
  | 'skipped-no-id';

export type SocketMessageRecord = {
  at: number;
  phone: string;
  messageId: number | null;
  direction: string;
  outcome: SocketMessageOutcome;
};

export type SocketHealthSnapshot = {
  log: SocketHealthEntry[];
  metrics: SocketHealthMetrics;
  registeredScopes: Record<string, number>;
  probe: { state: string; connectionId: string | null } | null;
  sessionStartedAt: number;
  /** ההודעות האחרונות שהגיעו בסוקט + מה קרה לכל אחת (מוגן ממחיקת-רעש) */
  messages: SocketMessageRecord[];
};

const LOG_STORAGE_KEY = 'funner:socket-health:log:v1';
const METRICS_STORAGE_KEY = 'funner:socket-health:metrics:v1';
const MESSAGES_STORAGE_KEY = 'funner:socket-health:messages:v1';

const MAX_LOG_ENTRIES = 500;
//* הוגדל מ-50 (14.10) — תקלות הסנכרון ספורדיות, וחוצץ קטן נמחק לפני שמגיעים לייצא
const MAX_MESSAGE_RECORDS = 300;
/** בעת גלישה מהמכסה — קודם נזרקים אירועי info שוטפים, ורק אחר כך רשומות חשובות */
const EVICT_FIRST_CATEGORIES: SocketHealthCategory[] = ['event', 'watchdog', 'sync', 'listeners'];
/** רשומות browser רועשות (מעברי טאב) שנזרקות ראשונות — הן שמחקו את כל האירועים בייצואים */
const EVICT_FIRST_BROWSER_TYPES = ['tab-visible', 'tab-hidden'];
const PERSIST_THROTTLE_MS = 1500;
/** איחוד אירועים זהים סמוכים (למשל צרור הודעות) לשורת יומן אחת */
const COALESCE_WINDOW_MS = 5000;

const WATCHDOG_INTERVAL_MS = 15_000;
/** סטייה בטיימר מעבר לזה נחשבת להשהיית טיימרים ע"י הדפדפן */
const TIMER_DRIFT_WARN_MS = 20_000;
/** שקט מוחלט בזמן Connected שמעבר לו נרשם חשד לסוקט זומבי */
const SILENT_CONNECTED_WARN_MS = 10 * 60_000;
//* מעצור ל-reconnect היזום של ה-watchdog — לעולם לא יותר מאחת ל-15 דקות
const FORCED_RECONNECT_COOLDOWN_MS = 15 * 60_000;
let lastForcedReconnectAt = 0;

const emptyMetrics = (): SocketHealthMetrics => ({
  pageLoads: 0,
  connectSuccess: 0,
  connectFailures: 0,
  unexpectedCloses: 0,
  reconnects: 0,
  revivals: 0,
  eventsTotal: 0,
  eventCounts: {},
  lastEventAt: null,
  lastEventType: null,
  lastConnectedAt: null,
  lastDisconnectedAt: null,
  timerThrottleDetections: 0,
  silentConnectedSuspicions: 0,
  noListenersWarnings: 0,
  handlerErrors: 0,
  resyncs: 0,
  updatedAt: Date.now(),
});

function safeRead<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // חריגת מכסה וכד' — הניטור לעולם לא מפיל את האפליקציה
  }
}

let log: SocketHealthEntry[] = safeRead<SocketHealthEntry[]>(LOG_STORAGE_KEY) ?? [];
let metrics: SocketHealthMetrics = { ...emptyMetrics(), ...(safeRead<Partial<SocketHealthMetrics>>(METRICS_STORAGE_KEY) ?? {}) };
let messageRecords: SocketMessageRecord[] = safeRead<SocketMessageRecord[]>(MESSAGES_STORAGE_KEY) ?? [];

const sessionStartedAt = Date.now();
const listeners = new Set<() => void>();
const registeredScopes: Record<string, number> = {};

let probe: SocketHealthProbe | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function notify(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* מאזין UI כושל לא עוצר את הניטור */
    }
  });
}

function persistNow(): void {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  metrics.updatedAt = Date.now();
  safeWrite(LOG_STORAGE_KEY, log);
  safeWrite(METRICS_STORAGE_KEY, metrics);
  safeWrite(MESSAGES_STORAGE_KEY, messageRecords);
}

function schedulePersist(immediate: boolean): void {
  if (immediate) {
    persistNow();
    return;
  }
  if (persistTimer) return;
  persistTimer = setTimeout(persistNow, PERSIST_THROTTLE_MS);
}

function isNoiseEntry(entry: SocketHealthEntry): boolean {
  if (entry.severity !== 'info') return false;
  if (EVICT_FIRST_CATEGORIES.includes(entry.category)) return true;
  return entry.category === 'browser' && EVICT_FIRST_BROWSER_TYPES.includes(entry.type);
}

function evictIfNeeded(): void {
  if (log.length <= MAX_LOG_ENTRIES) return;
  const overflow = log.length - MAX_LOG_ENTRIES;
  let removed = 0;
  // היומן מוחזק מהחדש לישן — מחיקה מהסוף (הישן ביותר), קודם רשומות "רועשות"
  for (let i = log.length - 1; i >= 0 && removed < overflow; i--) {
    if (isNoiseEntry(log[i])) {
      log.splice(i, 1);
      removed++;
    }
  }
  if (log.length > MAX_LOG_ENTRIES) {
    log = log.slice(0, MAX_LOG_ENTRIES);
  }
}

function truncateDetails(details?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!details) return undefined;
  const out: Record<string, unknown> = {};
  Object.entries(details).forEach(([k, v]) => {
    if (typeof v === 'string' && v.length > 300) {
      out[k] = `${v.slice(0, 300)}…`;
    } else {
      out[k] = v;
    }
  });
  return out;
}

export function recordSocketHealth(
  category: SocketHealthCategory,
  type: string,
  severity: SocketHealthSeverity = 'info',
  details?: Record<string, unknown>,
): void {
  const now = Date.now();

  // איחוד אירועים שוטפים זהים סמוכים (צרור הודעות / re-bind של מאזינים → שורה אחת עם מונה)
  if (category === 'event' || (category === 'listeners' && type === 'rebound')) {
    const head = log[0];
    if (
      head &&
      head.category === category &&
      head.type === type &&
      now - head.at <= COALESCE_WINDOW_MS
    ) {
      head.at = now;
      head.count = (head.count ?? 1) + 1;
      schedulePersist(false);
      notify();
      return;
    }
  }

  const entry: SocketHealthEntry = {
    id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
    at: now,
    category,
    type,
    severity,
    details: truncateDetails(details),
  };
  log = [entry, ...log];
  evictIfNeeded();
  // רשומות חיבור / אזהרות נשמרות מיידית — כדי לא לאבד אותן אם הטאב נהרג
  schedulePersist(category === 'connection' || severity !== 'info');
  notify();
}

/** אירוע נכנס מהסוקט. scope: 'global' — המאזין האפליקטיבי; 'chat' — צ'אט פתוח. */
export function noteSocketEvent(eventName: string, scope: string): void {
  metrics.lastEventAt = Date.now();
  metrics.lastEventType = eventName;
  silentWarningIssued = false;
  if (scope === 'global') {
    metrics.eventsTotal += 1;
    metrics.eventCounts[eventName] = (metrics.eventCounts[eventName] ?? 0) + 1;
    recordSocketHealth('event', eventName, 'info', { scope });
  } else {
    schedulePersist(false);
    notify();
  }
}

/** אירוע התקבל אך המטפל שלו זרק שגיאה — עדכון שלא השתקף ב-UI. */
export function noteSocketHandlerError(eventName: string, scope: string, error: unknown): void {
  metrics.handlerErrors += 1;
  recordSocketHealth('error', 'handler-error', 'error', {
    event: eventName,
    scope,
    message: error instanceof Error ? error.message : String(error),
  });
}

/** תוצאת הטיפול בהודעת צ'אט מהסוקט — נשמרת במאגר הייעודי המוגן ממחיקת-רעש. */
export function noteMessageOutcome(
  phone: string,
  messageId: number | null,
  direction: string,
  outcome: SocketMessageOutcome,
): void {
  messageRecords = [{ at: Date.now(), phone, messageId, direction, outcome }, ...messageRecords];
  if (messageRecords.length > MAX_MESSAGE_RECORDS) {
    messageRecords = messageRecords.slice(0, MAX_MESSAGE_RECORDS);
  }
  //* זריקה שקטה היא אנומליה — מקבלת גם רשומה בולטת ביומן הראשי; recovery הוא אזהרה.
  if (outcome === 'dropped-empty-company-tag' || outcome === 'dropped-foreign-company') {
    recordSocketHealth('error', `message-${outcome}`, 'error', { phone, messageId });
  } else if (outcome === 'contact-missing-recovery' || outcome === 'contact-missing-recovery-chat-applied') {
    recordSocketHealth('sync', 'message-contact-recovery', 'warning', { phone, messageId });
  } else {
    schedulePersist(false);
    notify();
  }
}

//* ===== ריפוי עצמי (14.10) — ייצוא קייסי הוכיח שהסקופים מתים בנפרד =====
//* (chat: 0 עם global: 23 וסוקט מחובר; 158 אירועי no-global-listeners). במקום רק
//* להתריע, ה-watchdog מפעיל "מחייאים": כל useSocketListeners רושם פונקציית bind
//* מחדש לסקופ שלו, וסוקט זומבי (silent-connected) מקבל reconnect יזום מהקונטקסט.
type SocketReviver = () => void;
const listenerRevivers = new Map<string, Set<SocketReviver>>();

export function registerListenerReviver(scope: string, fn: SocketReviver): () => void {
  let set = listenerRevivers.get(scope);
  if (!set) {
    set = new Set();
    listenerRevivers.set(scope, set);
  }
  set.add(fn);
  return () => {
    set?.delete(fn);
    if (set && set.size === 0) listenerRevivers.delete(scope);
  };
}

let reconnectRequester: SocketReviver | null = null;
export function registerSocketReconnector(fn: SocketReviver): () => void {
  reconnectRequester = fn;
  return () => {
    if (reconnectRequester === fn) reconnectRequester = null;
  };
}

/** הסרה+רישום-מחדש מיידיים של מאזינים הם re-bind שגרתי של React — מאוחדים לשורה
 *  אחת עם מונה, כדי שהצמדים הרועשים לא ימחקו את שאר היומן (כפי שקרה בייצואי 21/7). */
const LISTENER_REBOUND_WINDOW_MS = 1000;
const pendingListenerRemovals = new Map<string, { eventsCount: number; timer: ReturnType<typeof setTimeout> }>();

export function noteListenersRegistered(scope: string, events: string[]): void {
  registeredScopes[scope] = events.length;
  const pending = pendingListenerRemovals.get(scope);
  if (pending) {
    clearTimeout(pending.timer);
    pendingListenerRemovals.delete(scope);
    recordSocketHealth('listeners', 'rebound', 'info', { scope, eventsCount: events.length });
    return;
  }
  recordSocketHealth('listeners', 'registered', 'info', { scope, eventsCount: events.length });
}

export function noteListenersRemoved(scope: string, events: string[]): void {
  registeredScopes[scope] = 0;
  const existing = pendingListenerRemovals.get(scope);
  if (existing) clearTimeout(existing.timer);
  const timer = setTimeout(() => {
    pendingListenerRemovals.delete(scope);
    //* הסרה שלא לוותה ברישום-מחדש — מצב אמיתי של "אין מאזינים" (דפוס FIX #2), מוצג כאזהרה
    recordSocketHealth('listeners', 'removed', 'warning', { scope, eventsCount: events.length });
  }, LISTENER_REBOUND_WINDOW_MS);
  pendingListenerRemovals.set(scope, { eventsCount: events.length, timer });
}

export function noteResync(source: string): void {
  metrics.resyncs += 1;
  recordSocketHealth('sync', 'data-resync', 'info', { source });
}

/** רישום מדדי חיבור — נקרא מ-SignalRContext בנקודות מחזור החיים. */
export const socketConnectionHealth = {
  startAttempt(attempt: number, isRecovery: boolean): void {
    recordSocketHealth('connection', 'start-attempt', 'info', { attempt, isRecovery });
  },
  connected(connectionId: string | null, isRecovery: boolean): void {
    metrics.connectSuccess += 1;
    metrics.lastConnectedAt = Date.now();
    if (isRecovery) metrics.revivals += 1;
    silentWarningIssued = false;
    recordSocketHealth('connection', isRecovery ? 'revived' : 'connected', 'info', {
      connectionId,
    });
  },
  startFailed(attempt: number, error: unknown): void {
    metrics.connectFailures += 1;
    recordSocketHealth('connection', 'start-failed', 'warning', {
      attempt,
      message: error instanceof Error ? error.message : String(error),
    });
  },
  reconnecting(error: unknown): void {
    recordSocketHealth('connection', 'reconnecting', 'warning', {
      message: error instanceof Error ? error.message : error == null ? null : String(error),
    });
  },
  reconnected(connectionId: string | null): void {
    metrics.reconnects += 1;
    metrics.lastConnectedAt = Date.now();
    silentWarningIssued = false;
    recordSocketHealth('connection', 'reconnected', 'info', { connectionId });
  },
  closed(error: unknown, intentional: boolean): void {
    metrics.lastDisconnectedAt = Date.now();
    if (!intentional) metrics.unexpectedCloses += 1;
    recordSocketHealth('connection', intentional ? 'stopped' : 'closed-unexpectedly', intentional ? 'info' : 'error', {
      message: error instanceof Error ? error.message : error == null ? null : String(error),
    });
  },
};

export function noteBrowserEvent(type: string, details?: Record<string, unknown>): void {
  recordSocketHealth('browser', type, type === 'offline' ? 'warning' : 'info', details);
}

/** רישום בדיקת מצב חי מ-SignalRContext (state + connectionId). */
export function registerSocketHealthProbe(fn: SocketHealthProbe): () => void {
  probe = fn;
  return () => {
    if (probe === fn) probe = null;
  };
}

export function subscribeSocketHealth(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getSocketHealthSnapshot(): SocketHealthSnapshot {
  let probeResult: SocketHealthSnapshot['probe'] = null;
  try {
    probeResult = probe ? probe() : null;
  } catch {
    probeResult = null;
  }
  return {
    log: [...log],
    metrics: { ...metrics, eventCounts: { ...metrics.eventCounts } },
    registeredScopes: { ...registeredScopes },
    probe: probeResult,
    sessionStartedAt,
    messages: [...messageRecords],
  };
}

export function clearSocketHealthLog(): void {
  log = [];
  metrics = emptyMetrics();
  metrics.pageLoads = 1; // הסשן הנוכחי
  messageRecords = [];
  persistNow();
  notify();
}

export function exportSocketHealthReport(): string {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      snapshot: getSocketHealthSnapshot(),
    },
    null,
    2,
  );
}

// ============|| Watchdog — זיהוי אנומליות ברקע ||============

let silentWarningIssued = false;
let noListenersWarningIssued = false;
let lastSampledState: string | null = null;
let lastTickAt = Date.now();

function watchdogTick(): void {
  const now = Date.now();
  const drift = now - lastTickAt - WATCHDOG_INTERVAL_MS;
  lastTickAt = now;

  // (1) השהיית טיימרים — הטיק איחר משמעותית → הטאב ישן / הדפדפן חנק טיימרים.
  //     בדיוק בתרחיש הזה ה-keepAlive של SignalR לא רץ והחיבור עלול למות בשקט.
  if (drift > TIMER_DRIFT_WARN_MS) {
    metrics.timerThrottleDetections += 1;
    recordSocketHealth('watchdog', 'timer-throttle', 'warning', {
      driftMs: drift,
      visibility: typeof document !== 'undefined' ? document.visibilityState : null,
    });
  }

  let currentState: string | null = null;
  let connectionId: string | null = null;
  try {
    const p = probe ? probe() : null;
    currentState = p?.state ?? null;
    connectionId = p?.connectionId ?? null;
  } catch {
    currentState = null;
  }

  // (2) שינוי מצב חיבור שלא לווה ברשומת connection (מעבר "שקט")
  if (currentState !== null && lastSampledState !== null && currentState !== lastSampledState) {
    recordSocketHealth('watchdog', 'state-changed', 'info', {
      from: lastSampledState,
      to: currentState,
      connectionId,
    });
  }
  if (currentState !== null) lastSampledState = currentState;

  // (3) חשד לסוקט זומבי — מדווח Connected אבל שקט מוחלט זמן רב
  //* השקט נמדד מהמאוחר מבין: האירוע האחרון, החיבור האחרון ותחילת הסשן —
  //* lastEventAt נשמר ב-localStorage, ומדידה ממנו לבדו סימנה חיבור טרי כ"זומבי"
  //* (ובצירוף ה-reconnect היזום יצרה לולאת רענון כל 15ש בשעות שקטות; תוקן 14.10).
  const activityBaseline = Math.max(
    metrics.lastEventAt ?? 0,
    metrics.lastConnectedAt ?? 0,
    sessionStartedAt,
  );
  if (
    currentState === 'Connected' &&
    !silentWarningIssued &&
    now - activityBaseline > SILENT_CONNECTED_WARN_MS
  ) {
    silentWarningIssued = true;
    metrics.silentConnectedSuspicions += 1;
    recordSocketHealth('watchdog', 'silent-connected', 'warning', {
      silentForMs: now - activityBaseline,
      lastEventType: metrics.lastEventType,
    });
    //* ריפוי עצמי: חשד זומבי → reconnect יזום (stop → onclose מחייה + resync).
    //* מעצור כפול: פעם אחת לרצף שקט + לכל היותר אחת ל-15 דקות.
    if (reconnectRequester && now - lastForcedReconnectAt > FORCED_RECONNECT_COOLDOWN_MS) {
      lastForcedReconnectAt = now;
      recordSocketHealth('watchdog', 'forced-reconnect', 'warning', {
        silentForMs: now - activityBaseline,
      });
      try {
        reconnectRequester();
      } catch {
        /* הכשל יתועד בערוצי החיבור הרגילים */
      }
    }
  }

  // (4) חיבור פעיל אבל המאזינים הגלובליים לא רשומים — אירועים מגיעים ולא מטופלים.
  //     נרשם פעם אחת לכל רצף (ולא כל טיק) כדי לא להציף את היומן.
  if (currentState === 'Connected' && (registeredScopes.global ?? 0) === 0) {
    if (!noListenersWarningIssued) {
      noListenersWarningIssued = true;
      metrics.noListenersWarnings += 1;
      recordSocketHealth('watchdog', 'no-global-listeners', 'error', {
        registeredScopes: { ...registeredScopes },
      });
    }
  } else if ((registeredScopes.global ?? 0) > 0) {
    noListenersWarningIssued = false;
  }

  // (5) ריפוי עצמי של מאזינים — סקופ רשום-מחייאים (רכיב חי) שמונה המאזינים שלו 0
  //     בזמן חיבור פעיל: רישום מחדש מיידי במקום להמתין לרענון ידני של המשתמש.
  if (currentState === 'Connected') {
    for (const [scope, revivers] of listenerRevivers) {
      if ((registeredScopes[scope] ?? 0) === 0 && revivers.size > 0) {
        recordSocketHealth('watchdog', 'listeners-revived', 'warning', {
          scope,
          reviversCount: revivers.size,
        });
        revivers.forEach((fn) => {
          try {
            fn();
          } catch {
            /* כשל ברישום מחדש — הטיק הבא ינסה שוב */
          }
        });
      }
    }
  }
}

function initMonitor(): void {
  if (typeof window === 'undefined') return;

  metrics.pageLoads += 1;
  recordSocketHealth('browser', 'page-load', 'info', {
    visibility: document.visibilityState,
    online: navigator.onLine,
  });

  setInterval(watchdogTick, WATCHDOG_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    noteBrowserEvent(document.visibilityState === 'visible' ? 'tab-visible' : 'tab-hidden');
    if (document.visibilityState === 'hidden') persistNow();
  });
  window.addEventListener('online', () => noteBrowserEvent('online'));
  window.addEventListener('offline', () => noteBrowserEvent('offline'));
  window.addEventListener('pagehide', () => persistNow());
}

initMonitor();
